import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Chip,
  Checkbox,
  FormControlLabel,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { toast } from "react-toastify";
import { usePermissions } from "../../login/PermissionContext";
import {
  fetchDischargeProcess,
  advanceDischargeProcess,
  updateDischargeChecklist,
  updateMccd,
  STAGE_LABELS,
} from "../api/dischargeProcessApi";
import MccdForm, {
  EMPTY_MCCD,
  mccdFormFromProcess,
  mccdFormToPayload,
} from "./MccdForm";
import SummaryStatusBand from "./SummaryStatusBand";
import { formatIstDateTime } from "../../../utils/istDateTime";

// Fixed pipeline order — must match main/ipd/manage_discharge_process.py's
// _PIPELINE exactly.
const STAGE_ORDER = ["ward", "billing", "nursing_discharge", "physical_discharge"];

// Role required to advance OUT of each stage (UX-only gating — the backend
// is the real authorization boundary). Mirrors the substring-matching,
// fail-open convention established by ../utils/bedDashboardRoles.js.
// physical_discharge has no entry: it's terminal, nothing to advance out of.
// These must stay in step with the tenant's DischargeStageRule rows — the
// backend is the real authorization boundary, and a keyword listed here that
// the rule table doesn't grant only buys the user an Advance button that
// errors.
// "facility user" is spelled out rather than a bare "user": the substring match
// would otherwise also admit "lab user" / "pharmacy user". Mirrors the
// DischargeStageRule seed exactly.
//
// nursing_discharge is listed for completeness, but the keyword match is NOT how
// a nurse is recognised — see the actingAsNurse prop below.
const STAGE_ADVANCE_ROLE_KEYWORDS = {
  ward: ["doctor"],
  billing: ["admin", "facility user", "front desk", "reception"],
  nursing_discharge: ["nurse", "nursing"],
};

const STAGE_ADVANCE_ROLE_LABELS = {
  ward: "Doctor",
  billing: "Billing desk",
  nursing_discharge: "Nurse",
};

// Timestamp field marking when the process ENTERED each stage — i.e. the
// previous stage's completion stamp. Used to tell a viewer who can't act on
// the current stage how long it has been waiting on someone else. "ward" is
// the first stage, so nothing precedes it.
const STAGE_ENTERED_AT_FIELD = {
  billing: "ward_completed_at",
  nursing_discharge: "billing_completed_at",
  physical_discharge: "physical_discharge_completed_at",
};

// `actingAsNurse` exists because a nurse's Role.name is NOT "nurse". Nurses are
// NurseRecord-backed; the role this screen can see is typically "facility user".
// So the keyword match above would hide the Nursing Discharge controls from the
// one person who is supposed to use them. The nurse dashboard, which only ever
// renders for a nurse, asserts it explicitly instead. This is UX-only either
// way: the backend authorizes against DischargeStageRule + an active
// NurseBedAssignment, and rejects anyone else regardless of what we render.
function isAdvanceAllowedForRole(stage, roleName, actingAsNurse) {
  if (actingAsNurse) return stage === "nursing_discharge";
  if (stage === "nursing_discharge") return false;
  if (!roleName) return true;
  const keywords = STAGE_ADVANCE_ROLE_KEYWORDS[stage];
  if (!keywords) return true;
  const normalized = String(roleName).trim().toLowerCase();
  return keywords.some((keyword) => normalized.includes(keyword));
}

function formatEnteredAt(dischargeProcess, stage) {
  const value = dischargeProcess[STAGE_ENTERED_AT_FIELD[stage]];
  if (!value) return null;
  // The stage_entered_at columns are UTC instants that arrive unmarked, so a
  // bare new Date() read them as local and stamped every discharge stage
  // 5h30m early.
  return formatIstDateTime(value, null) || null;
}

const DischargeStageTracker = ({
  admissionId,
  onAdvanced,
  actingAsNurse = false,
  onOpenSummary,
}) => {
  const { userInfo } = usePermissions();
  const roleName = userInfo?.role_permission?.[0]?.role;

  // undefined = loading, null = no active discharge process for this admission
  const [dischargeProcess, setDischargeProcess] = useState(undefined);
  const [advancing, setAdvancing] = useState(false);
  const [savingChecklist, setSavingChecklist] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  // Doctor's MCCD certification dialog (Ward stage, deceased only).
  const [certifyOpen, setCertifyOpen] = useState(false);
  const [certifyForm, setCertifyForm] = useState(EMPTY_MCCD);
  const [certifySaving, setCertifySaving] = useState(false);

  const isDeceased = dischargeProcess?.outcome === "deceased";
  // Billing's clearance is gated on this server-side, so it is the doctor's
  // real precondition for handing over — not a status decoration.
  const summaryApproved = dischargeProcess?.summary_status === "approved";
  // Server-computed: which of the four certifying fields are still missing. Empty
  // on a living patient.
  const deathRecordMissing = dischargeProcess?.death_record_incomplete || [];

  const openCertify = () => {
    setCertifyForm(mccdFormFromProcess(dischargeProcess));
    setCertifyOpen(true);
  };

  const handleCertifySave = async () => {
    setCertifySaving(true);
    try {
      const res = await updateMccd(dischargeProcess.id, mccdFormToPayload(certifyForm));
      if (res?.status === "Success") {
        toast.success(res.message || "MCCD saved.");
        // Non-blocking hygiene warnings (mode of dying as an immediate cause,
        // multiple conditions on a line) — surface them, but the save stood.
        (res.warnings || []).forEach((w) => toast.warn(w));
        setDischargeProcess(res.data || dischargeProcess);
        setCertifyOpen(false);
      } else {
        toast.error(res?.message || "Could not save the MCCD.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not save the MCCD.");
    } finally {
      setCertifySaving(false);
    }
  };

  const loadDischargeProcess = useCallback(() => {
    if (!admissionId) return;
    setDischargeProcess(undefined);
    fetchDischargeProcess(admissionId)
      .then((res) => setDischargeProcess(res?.data || null))
      .catch(() => setDischargeProcess(null));
  }, [admissionId]);

  useEffect(() => {
    loadDischargeProcess();
  }, [loadDischargeProcess]);

  // Ticking an item saves immediately — a checklist that silently loses ticks
  // because the nurse closed the panel without a Save press would be worse
  // than useless. The row (with its recomputed checklist_incomplete) is echoed
  // back, so the Advance gate below updates from the server's answer, not ours.
  const handleChecklistToggle = async (key, checked) => {
    if (!dischargeProcess) return;
    const previous = dischargeProcess;
    // Optimistic: reflect the tick instantly, roll back if the save fails.
    setDischargeProcess({
      ...dischargeProcess,
      discharge_checklist: { ...(dischargeProcess.discharge_checklist || {}), [key]: checked },
    });
    setSavingChecklist(true);
    try {
      const res = await updateDischargeChecklist(dischargeProcess.id, { [key]: checked });
      if (res?.status === "Success" && res.data) {
        setDischargeProcess(res.data);
      } else {
        setDischargeProcess(previous);
        toast.error(res?.message || "Could not save the checklist.");
      }
    } catch (err) {
      setDischargeProcess(previous);
      toast.error(err?.response?.data?.message || "Could not save the checklist.");
    } finally {
      setSavingChecklist(false);
    }
  };

  // For a deceased patient, physical discharge IS release of the body. The
  // backend refuses to release one on an incomplete death record (time of death,
  // cause, manner, certifying doctor) unless a reason is given — so ask for it
  // here rather than letting the nurse hit an error she can't act on.
  const handleAdvance = async (deathRecordOverrideReason) => {
    if (!dischargeProcess) return;
    setAdvancing(true);
    try {
      const res = await advanceDischargeProcess(dischargeProcess.id, {
        deathRecordOverrideReason,
      });
      if (res?.status === "Success") {
        toast.success(res.message || "Discharge process advanced.");
        setDischargeProcess(res.data || dischargeProcess);
        if (onAdvanced) onAdvanced(res.data);
      } else {
        toast.error(res?.message || "Could not advance discharge process.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not advance discharge process.");
    } finally {
      setAdvancing(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Discharge Stage
      </Typography>

      {dischargeProcess === undefined ? (
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      ) : !dischargeProcess ? (
        <Typography variant="body2" color="text.secondary">
          Not initiated
        </Typography>
      ) : dischargeProcess.stage === "cancelled" ? (
        <Chip size="small" label="Cancelled" />
      ) : dischargeProcess.stage === "ward" &&
        isAdvanceAllowedForRole("ward", roleName, actingAsNurse) ? (
        // The doctor's own step. Deliberately NO stepper here: the stages after
        // this one belong to the billing desk and the nurse and are not the
        // doctor's business — showing them made this read as a status board
        // rather than a thing to DO.
        //
        // The checklist used to live here and gate this button. It moved to the
        // nurse's stage (2026-07-14): every required item on it is nursing work,
        // so the doctor was certifying things the nurse actually did.
        <>
          <SummaryStatusBand
            summaryStatus={dischargeProcess.summary_status}
            onOpenSummary={onOpenSummary}
          />

          {/* outcome is write-once — no endpoint updates it — so a discharge
              initiated with it blank can never reach the death-certification
              pathway. Cancelling and re-initiating is the only recovery. */}
          {!dischargeProcess.outcome && (
            <Typography variant="body2" color="warning.main" sx={{ mb: 1.5 }}>
              Outcome was not recorded when this discharge was initiated. If the
              patient is deceased, cancel this discharge and re-initiate it — the
              outcome cannot be changed afterwards.
            </Typography>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {isDeceased
              ? "Certify the death, then hand the case to the billing desk for account closure."
              : "Hand the case to the billing desk for account closure."}
          </Typography>

          {/* Death certification is the doctor's clinical act, at the doctor's
              step. The case cannot leave the ward until it is done — the backend
              enforces the same gate, so this is not merely cosmetic. */}
          {isDeceased && (
            <Box
              sx={{
                border: 1,
                borderColor: deathRecordMissing.length > 0 ? "warning.main" : "success.light",
                borderRadius: 1,
                p: 1.5,
                mb: 1.5,
              }}
            >
              <Typography variant="body2" sx={{ mb: 1 }}>
                {deathRecordMissing.length > 0 ? (
                  <>
                    Death not yet certified — <strong>{deathRecordMissing.join(", ")}</strong>{" "}
                    still to be recorded.
                  </>
                ) : (
                  <>Death certified.</>
                )}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                color={deathRecordMissing.length > 0 ? "warning" : "inherit"}
                disabled={advancing}
                onClick={openCertify}
              >
                {deathRecordMissing.length > 0 ? "Certify Death" : "Edit Certification"}
              </Button>
            </Box>
          )}

          {/* Gated on the summary because billing's clearance is gated on it
              server-side. Without this the doctor advanced, was told their part
              was done, and the discharge stalled at a desk that could not move
              it. */}
          <Button
            variant="contained"
            size="small"
            disabled={
              advancing ||
              !summaryApproved ||
              (isDeceased && deathRecordMissing.length > 0)
            }
            onClick={() => handleAdvance()}
          >
            {advancing ? "Handing over…" : "Complete & Send to Billing"}
          </Button>
        </>
      ) : dischargeProcess.stage === "nursing_discharge" &&
        isAdvanceAllowedForRole("nursing_discharge", roleName, actingAsNurse) ? (
        // The nurse's own step, and the last one. Billing is cleared; what
        // remains is the physical act of discharging the patient. Completing
        // this is what closes the admission and frees the bed.
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {isDeceased
              ? "Billing is cleared. Complete the checklist, then release the body — this frees the bed and closes the admission."
              : "Billing is cleared. Complete the discharge checklist, then mark the patient physically discharged — this frees the bed and closes the admission."}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", mb: 1 }}>
            {(dischargeProcess.checklist_items || []).map((item) => (
              <FormControlLabel
                key={item.key}
                control={
                  <Checkbox
                    size="small"
                    disabled={savingChecklist || advancing}
                    checked={Boolean(
                      (dischargeProcess.discharge_checklist || {})[item.key]
                    )}
                    onChange={(e) => handleChecklistToggle(item.key, e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    {item.label}
                    {!item.required && (
                      <Typography component="span" variant="caption" color="text.secondary">
                        {" "}
                        (optional)
                      </Typography>
                    )}
                  </Typography>
                }
              />
            ))}
          </Box>

          {(dischargeProcess.checklist_incomplete || []).length > 0 && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
              {dischargeProcess.checklist_incomplete.length} required item
              {dischargeProcess.checklist_incomplete.length === 1 ? "" : "s"} left.
            </Typography>
          )}

          {/* A deceased patient with an incomplete death record. Releasing the
              body is still possible — it must be, or a body could be stuck in the
              ward over a dropdown — but it takes a reason, and the reason is kept. */}
          {deathRecordMissing.length > 0 && (
            <Box
              sx={{
                border: 1,
                borderColor: "error.light",
                bgcolor: "error.lighter",
                borderRadius: 1,
                p: 1.5,
                mb: 1.5,
              }}
            >
              <Typography variant="body2" sx={{ mb: 1 }}>
                The death record is incomplete — <strong>{deathRecordMissing.join(", ")}</strong>{" "}
                {deathRecordMissing.length === 1 ? "is" : "are"} missing. Complete it
                in the Death Register, or give a reason to release the body anyway.
              </Typography>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={2}
                label="Reason for releasing on an incomplete record"
                value={overrideReason}
                disabled={advancing}
                onChange={(e) => setOverrideReason(e.target.value)}
              />
            </Box>
          )}

          <Button
            variant="contained"
            size="small"
            color="success"
            disabled={
              advancing ||
              savingChecklist ||
              (dischargeProcess.checklist_incomplete || []).length > 0 ||
              (deathRecordMissing.length > 0 && !overrideReason.trim())
            }
            onClick={() => handleAdvance(overrideReason.trim() || undefined)}
          >
            {advancing
              ? "Discharging…"
              : isDeceased
              ? "Release Body & Complete Discharge"
              : "Mark Physically Discharged"}
          </Button>
        </>
      ) : (
        <>
          {/* Also shown after the hand-off: rows stranded at billing with an
              unapproved summary already exist, and this is their route out. */}
          <SummaryStatusBand
            summaryStatus={dischargeProcess.summary_status}
            onOpenSummary={onOpenSummary}
          />

          <Stepper activeStep={Math.max(0, STAGE_ORDER.indexOf(dischargeProcess.stage))}>
            {STAGE_ORDER.map((stageKey) => (
              <Step key={stageKey}>
                <StepLabel
                  optional={
                    STAGE_ADVANCE_ROLE_LABELS[stageKey] ? (
                      <Typography variant="caption" color="text.secondary">
                        {STAGE_ADVANCE_ROLE_LABELS[stageKey]}
                      </Typography>
                    ) : null
                  }
                >
                  {STAGE_LABELS[stageKey] || stageKey}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ mt: 2 }}>
            {dischargeProcess.stage === "physical_discharge" ? (
              <Chip size="small" color="success" label="Discharge complete" />
            ) : isAdvanceAllowedForRole(dischargeProcess.stage, roleName, actingAsNurse) ? (
              <Button
                variant="contained"
                size="small"
                disabled={advancing}
                onClick={() => handleAdvance()}
              >
                {advancing ? "Advancing..." : "Advance"}
              </Button>
            ) : (
              // The viewer's own part is done — this stage belongs to someone
              // else. Phrased as a hand-off, not as a permission refusal: the
              // doctor who just advanced out of Ward has finished their role
              // in the discharge and should not read this as being blocked.
              <Typography variant="caption" color="text.secondary" component="div">
                {(() => {
                  const stage = dischargeProcess.stage;
                  const owner = STAGE_ADVANCE_ROLE_LABELS[stage];
                  const enteredAt = formatEnteredAt(dischargeProcess, stage);
                  const handoff = `Handed to ${STAGE_LABELS[stage] || stage}${
                    enteredAt ? ` on ${enteredAt}` : ""
                  }`;
                  // An unapproved summary means the holdup is still the
                  // doctor's, so this must not read as "awaiting someone else".
                  if (!summaryApproved) {
                    return `${handoff} — blocked on the discharge summary above.`;
                  }
                  return owner
                    ? `${handoff} — awaiting ${owner} clearance.`
                    : `${handoff}.`;
                })()}
              </Typography>
            )}
          </Box>
        </>
      )}

      {/* Doctor's MCCD certification (Ward stage). The structured cause of death;
          time of death may already be filled by the nurse at initiation. */}
      <Dialog open={certifyOpen} onClose={() => setCertifyOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Certify Death — Medical Certificate of Cause of Death (Form 4)</DialogTitle>
        <DialogContent dividers>
          <MccdForm
            value={certifyForm}
            onChange={(patch) => setCertifyForm((prev) => ({ ...prev, ...patch }))}
            disabled={certifySaving}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCertifyOpen(false)} disabled={certifySaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCertifySave} disabled={certifySaving}>
            {certifySaving ? "Saving…" : "Save Certification"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DischargeStageTracker;
